import React from 'react';

import { useNavigate } from 'react-router-dom';

import { reverse } from 'named-urls';

import { AxiosError } from 'axios';

import { Form, Formik, FormikHelpers } from 'formik';

import Record from 'ui/Record/Record';

import { MainHeader } from 'components/MainHeader/MainHeader';

import { Button } from 'components/Button';

import NavigationBlocker from 'components/NavigationBlocker';

import API from 'utils/api';

import { formErrors, getErrorMessage } from 'utils/api/errors';

import routes from 'core/routes';

import { useNavBarHeight } from 'utils/hooks/navbar';

import { useStore } from 'utils/hooks/store';

import { MembershipMode } from 'utils/api/modules/memberGroups';

import { isDynamicGroup } from 'containers/MemberGroups/utils/membership';
import MemberGroupForm, {
  emptyRule,
  MemberGroupFormValues,
  memberGroupValidationSchema,
} from '../components/MemberGroupForm';
import { serializeRule } from '../components/RuleBuilder/registry';
import {
  DetailContainer,
  EditActions,
  Section,
  SectionBody,
  SectionHeader,
  SectionTitle,
} from '../MemberGroupDetails/styles';

const INITIAL_VALUES: MemberGroupFormValues = {
  name: '',
  description: '',
  membershipMode: MembershipMode.Manual,
  members: [],
  rule: emptyRule(),
};

const MemberGroupCreate: React.FunctionComponent = (): React.ReactElement => {
  const navigate = useNavigate();
  const navBarHeight = useNavBarHeight();
  const { notification } = useStore();
  const shouldAllowNavigationRef = React.useRef(false);
  const [shouldAllowNavigation, setShouldAllowNavigation] = React.useState(false);

  const groupsPath = routes.dashboard.settings.groups.toString();

  const onSubmit = async (
    values: MemberGroupFormValues,
    helpers: FormikHelpers<MemberGroupFormValues>
  ): Promise<void> => {
    const isDynamic = isDynamicGroup(values);
    try {
      const { data } = await API.memberGroups.createMemberGroup({
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        membershipMode: values.membershipMode,
        ...(isDynamic ? { rule: serializeRule(values.rule) } : { memberUuids: values.members.map(({ uuid }) => uuid) }),
      })();
      notification.enqueueSuccessSnackbar(`${data.name} created`);
      // The blocker reads the ref because the state update below hasn't flushed by the time we navigate.
      shouldAllowNavigationRef.current = true;
      setShouldAllowNavigation(true);
      navigate(reverse(routes.dashboard.settings.groups.details, { groupId: data.uuid }));
    } catch (error) {
      const { fieldErrors } = formErrors(error as AxiosError);
      if (fieldErrors?.name?.length) {
        helpers.setFieldError('name', fieldErrors.name[0]);
      } else if (fieldErrors?.description?.length) {
        helpers.setFieldError('description', fieldErrors.description[0]);
      } else if (fieldErrors?.rule?.length) {
        helpers.setFieldError('rule', fieldErrors.rule[0]);
      } else {
        notification.enqueueErrorSnackbar(getErrorMessage(error as AxiosError, 'Could not create group'));
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };

  return (
    <Record
      key="member_group_create"
      navBarHeight={navBarHeight}
      Header={
        <MainHeader
          title="New group"
          breadcrumbs={[
            { label: 'Organisation', link: '' },
            { label: 'Groups', link: groupsPath },
            { label: 'New group', link: '' },
          ]}
        />
      }
      Content={() => (
        <Formik initialValues={INITIAL_VALUES} validationSchema={memberGroupValidationSchema} onSubmit={onSubmit}>
          {({ isSubmitting, dirty }) => (
            <Form>
              <NavigationBlocker
                when={dirty && !shouldAllowNavigation}
                shouldBlockNavigation={() => dirty && !shouldAllowNavigationRef.current}
                navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
              />
              <DetailContainer>
                <Section>
                  <SectionHeader>
                    <SectionTitle>Details</SectionTitle>
                  </SectionHeader>
                  <SectionBody>
                    <MemberGroupForm showTypeCards showMembers />
                    <EditActions>
                      <Button variant="outlined" size="large" type="button" onClick={() => navigate(groupsPath)}>
                        Cancel
                      </Button>
                      <Button variant="contained" size="large" type="submit" disabled={isSubmitting}>
                        Create group
                      </Button>
                    </EditActions>
                  </SectionBody>
                </Section>
              </DetailContainer>
            </Form>
          )}
        </Formik>
      )}
    />
  );
};

export default MemberGroupCreate;
